import { React, useEffect, useMemo, useRef, useState } from "react";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import Lottie from "react-lottie";
import LoadingDocument from "../../../../assets/gifs/loading-document.json";
import { lottieLoopOptions } from "../../../../utils/Utils";
import VerificationLayout from "../../../layout/VerificationLayout.component";
import { useVerification } from "../../../../context/Verification.context";
import HocPopup from "../../../../hoc/hocPopup";
import RenderArtifactContent from "./RenderArtifact.component";
import { useNavigate } from "react-router-dom";
import {
  withWalkthroughSlides,
  shouldShowKnowledgeHub,
  includesKnowledgeHubService,
  getPostPullNavTarget,
} from "../../../../utils/videoPlayer";

const InsurancePullComponent = ({
  status,
  isPullComplete,
  setISPullComplete,
  uiPhase,
}) => {
  const verification = useVerification();
  const navigate = useNavigate();

  // Never show the walkthrough on a repull — the user has already seen it
  const isRepull =
    verification?.isRepull === "true" || verification?.isRepull === true;

  const [close, setClose] = useState(false);
  const [saveResponse, setSaveResponse] = useState([]);

  // If knowledge hub conditions aren't met, navigate directly after pull completes
  useEffect(() => {
    if (
      !isRepull &&
      !verification?.isExpressLogin &&
      !verification?.isFromCustomerTable &&
      isPullComplete &&
      !shouldShowKnowledgeHub(verification)
    ) {
      navigate(getPostPullNavTarget(verification), { replace: true });
    }
  }, [isPullComplete, verification?.workflow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Express: after pull (insurance_id set), skip tick page → workflow / insurance-only target
  useEffect(() => {
    if (verification?.isExpressLogin && isPullComplete) {
      navigate(getPostPullNavTarget(verification), { replace: true });
    }
  }, [isPullComplete]); // eslint-disable-line react-hooks/exhaustive-deps

  const baseArtifacts = useMemo(
    () =>
      Array.isArray(verification?.artifacts)
        ? verification?.artifacts
        : verification?.artifacts?.items || [],
    [verification?.artifacts],
  );

  // After carrier/pull: KH WalkMe when session/dealership includes Knowledge Hub
  const includeWalkthroughRef = useRef(null);
  if (includeWalkthroughRef.current === null) {
    const hasKhService = includesKnowledgeHubService(verification);
    includeWalkthroughRef.current = {
      insurance: false,
      knowledgeHub: hasKhService,
    };
  }

  const artifactsWithWalkthrough = useMemo(
    () =>
      withWalkthroughSlides(baseArtifacts, verification, {
        includeInsurance: false,
        includeKnowledgeHub: includeWalkthroughRef.current.knowledgeHub,
        forceKnowledgeHub: includeWalkthroughRef.current.knowledgeHub,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseArtifacts, verification?.walkthroughVideoUrl],
  );

  const hasArtifactsList = baseArtifacts.length > 0;

  // Artifacts for combined / KH — not insurance-only
  const shouldShowArtifacts =
    hasArtifactsList &&
    includesKnowledgeHubService(verification) &&
    !close &&
    verification?.isRepull !== "true" &&
    verification?.isRepull !== true &&
    !verification?.isArtifactOpen;

  // ✅ FIXED: Always use LoadingDocument — tick/success animation removed entirely
  const lottieOptions = lottieLoopOptions;
  lottieOptions.loop = true;
  lottieOptions.animationData = LoadingDocument;

  const renderStatus = (status) => {
    if (status === "SUCCESS" || status === "PULLING_DATA") {
      return `Your policy information is almost complete. Only a couple more clicks to go`;
    } else if (status === "PROCESSING") {
      return `We're establishing contact with your carrier`;
    } else if (status === "GETTING_CONSUMERS") {
      return `We're in! We are now pulling all your policy information`;
    } else {
      return `STATUS : ${status} \n\nAuthorizing with servers. This can take a minute`;
    }
  };

  const renderLoadingScreen = () => (
    <VerificationLayout>
      <Box
        display={"flex"}
        flexDirection={"column"}
        height={"100%"}
        width={"100%"}
        overflow={window.innerWidth <= 750 ? "scroll" : "hidden"}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "20%",
            width: "100%",
          }}
        >
          <Typography variant="h6" align="center">
            {verification.insuranceObject.name}
          </Typography>
          <Typography
            variant="p"
            align="center"
            sx={{ marginTop: "8px", marginBottom: "24px" }}
          >
            {verification.insuranceObject.homepageUrl}
          </Typography>
        </Box>
        <Box
          sx={{
            height: "60%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-evenly",
          }}
        >
          <Lottie
            options={lottieOptions}
            height={window.innerWidth <= 750 ? 250 : 270}
            width={window.innerWidth <= 750 ? 250 : 270}
          />
        </Box>
        <Box
          display={"flex"}
          flexDirection={"column"}
          gap={2}
          justifyContent={"flex-end"}
          height={"20%"}
        >
          <Typography
            variant="p"
            align="center"
            sx={{
              whiteSpace: "pre-wrap",
              overflowWrap: "break-word",
              fontSize: "15px",
            }}
          >
            {renderStatus(status)}
          </Typography>
          {/* ✅ Always show the "30 seconds" hint — no more conditional hide on SUCCESS */}
          <Typography
            align="center"
            fontSize={"12px"}
            style={{
              padding: window.innerWidth <= 750 ? "12px" : "5px",
            }}
          >
            The entire process should take about 30 seconds +/-
          </Typography>
        </Box>
      </Box>
    </VerificationLayout>
  );

  const renderBackground = () => {
    // Express Login: show scanning/loading screen the whole time until
    // isPullComplete triggers navigation — the tick page is never shown.
    if (verification?.isExpressLogin) return renderLoadingScreen();

    // Walkthrough (if any) is slide 0 of the same artifact screen
    if (shouldShowArtifacts) {
      return (
        <RenderArtifactContent
          status={status}
          artifactsArray={artifactsWithWalkthrough}
          setClose={setClose}
          saveResponse={saveResponse}
          setSaveResponse={setSaveResponse}
          isPullComplete={isPullComplete}
          setISPullComplete={setISPullComplete}
        />
      );
    }

    // Normal pull-in-progress loading screen
    if (
      status === "PROCESSING" ||
      status === "GETTING_CONSUMERS" ||
      status === "PULLING_DATA" ||
      status === "SUCCESS" ||
      status === "NOT_AUTHENTICATED" ||
      verification?.isRepull === "true" ||
      verification?.isRepull === true ||
      close ||
      verification?.isArtifactOpen ||
      baseArtifacts.length <= 0 ||
      !includesKnowledgeHubService(verification) ||
      (verification?.isArtifactOpen && !uiPhase)
    ) {
      return renderLoadingScreen();
    }

    // Fallback — artifacts (safety net)
    return (
      <RenderArtifactContent
        status={status}
        artifactsArray={artifactsWithWalkthrough}
        setClose={setClose}
        saveResponse={saveResponse}
        setSaveResponse={setSaveResponse}
        isPullComplete={isPullComplete}
        setISPullComplete={setISPullComplete}
      />
    );
  };

  return <>{renderBackground()}</>;
};

export default HocPopup(InsurancePullComponent);
